import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';
import { usePageGuideStore } from '../../stores/pageGuideStore';
import { useUserStore } from '../../stores/userStore';
import { shouldAutoStart } from '../../utils/pageGuide';
import { guideChoice, type GuideEntry } from './guideChoice';

/**
 * ⭐ "?" — replay the guide of the page on screen (owner, 2026-10-10; wireframe frame 3).
 * In the desktop rail above Settings, and as "Guide for this page" in the phone drawer.
 * ⚠️ Shown ONLY while the open page has a guide — never a button that does nothing.
 * A dot while a guide on screen is unseen on this account (or rewritten since).
 * ⭐ With SEVERAL guides on screen (the page overview + the open tab's chapter) it
 * offers a choice — otherwise the overview could only be reached again by leaving
 * the page (owner, 2026-10-10).
 * ⭐ And a guide that knows where the person is (`here`, 2026-10-11 — the Life Map's
 * end-to-end guide) offers "From the start" or "From <section>": replay the whole
 * walk, or just the part for the tab on screen. Worked out at CLICK time — which tab
 * is open changes without this button re-rendering.
 */

type Entry = GuideEntry;

export function PageGuideButton({ variant, isCollapsed = false, onBeforeStart }: {
  variant: 'rail' | 'drawer';
  isCollapsed?: boolean;
  /** The drawer closes first, so the guide is not opened underneath it. */
  onBeforeStart?: () => void;
}) {
  const stack = usePageGuideStore(s => s.stack);
  const running = usePageGuideStore(s => !!s.active);
  const seen = useUserStore(s => s.pageGuidesSeen);
  const [menuOpen, setMenuOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  if (!stack.length) return null;
  // Overview first, then the chapters.
  const guides = [...stack].sort((a, b) => (a.level ?? 0) - (b.level ?? 0));
  const unseen = guides.some(g => shouldAutoStart(g, seen));
  const start = (entry: Entry) => {
    setMenuOpen(false);
    if (onBeforeStart) {
      onBeforeStart();
      // After the drawer has closed and handed focus back.
      window.setTimeout(() => usePageGuideStore.getState().start(entry.guide, entry.step), 250);
    } else usePageGuideStore.getState().start(entry.guide, entry.step);
  };
  const entries = guideChoice(stack).entries;
  const dot = (on: boolean) => on && <span aria-hidden="true" className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-sky-500" />;

  if (variant === 'drawer') {
    return (
      <div className="p-2 border-t border-border space-y-1">
        {entries.length > 1 && <p className="px-1 pb-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{guides.length > 1 ? 'Guides for this page' : guides[0].name}</p>}
        {entries.map(e => (
          <button
            key={`${e.guide.id}:${e.step}`}
            type="button"
            onClick={() => start(e)}
            disabled={running}
            className="flex min-h-[3rem] w-full items-center gap-3 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
          >
            <span className="relative shrink-0"><HelpCircle className="h-5 w-5" aria-hidden="true" />{dot(e.step === 0 && shouldAutoStart(e.guide, seen))}</span>
            <span className="truncate">{entries.length > 1 ? e.label : 'Guide for this page'}</span>
          </button>
        ))}
      </div>
    );
  }
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => {
          const now = guideChoice(stack).entries;
          if (now.length > 1) setMenuOpen(o => !o); else start(now[0]);
        }}
        disabled={running}
        aria-label="Guide for this page"
        aria-haspopup={entries.length > 1 ? 'menu' : undefined}
        aria-expanded={entries.length > 1 ? menuOpen : undefined}
        data-guide="nav.guide"
        title="Guide for this page (Shift + ?)"
        className="flex items-center gap-3 w-full px-3 py-2 rounded-md transition-colors text-muted-foreground hover:bg-secondary hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-card disabled:opacity-50"
      >
        <span className="relative shrink-0"><HelpCircle className="w-5 h-5" aria-hidden="true" />{dot(unseen)}</span>
        {!isCollapsed && <span className="nav-label-in text-sm truncate">Guide</span>}
      </button>
      {menuOpen && (
        <GuideMenu anchor={btnRef.current} {...guideChoice(stack)} seen={seen} onPick={start} onClose={() => setMenuOpen(false)} />
      )}
    </>
  );
}

/**
 * The choice, beside the rail button. Portalled with a fixed position — the rail
 * clips its own overflow. ⭐ With no Guide button on screen to sit beside (Shift+? on
 * a phone or tablet, where Guide is in the menu drawer) it is centred.
 */
export function GuideMenu({ anchor, title, entries, seen, onPick, onClose, anchorToggles = true }: {
  anchor: HTMLElement | null;
  /**
   * The anchor opened this menu and toggles it, so a press on it is not "outside".
   * ⚠️ `false` for Shift+?'s menu: pressing Guide then closes it, and Guide opens its
   * own — one menu, never two.
   */
  anchorToggles?: boolean;
  title: string;
  entries: Entry[];
  seen: Record<string, number> | undefined;
  onPick: (e: Entry) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onDown = (e: MouseEvent) => {
      if (ref.current?.contains(e.target as Node) || (anchorToggles && anchor?.contains(e.target as Node))) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); anchor?.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); };
  }, [anchor, onClose, anchorToggles]);
  const r = anchor?.getBoundingClientRect();
  const style: CSSProperties = r
    ? { left: r.right + 8, bottom: Math.max(8, window.innerHeight - r.bottom) }
    : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
  return createPortal(
    <div ref={ref} role="menu" aria-label={title} style={style}
      className="fixed z-[150] w-60 rounded-xl border border-border bg-card p-1 shadow-2xl">
      <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      {entries.map(e => (
        <button key={`${e.guide.id}:${e.step}`} type="button" role="menuitem" onClick={() => onPick(e)}
          className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-foreground transition-colors hover:bg-secondary focus:outline-none focus-visible:bg-secondary">
          <HelpCircle className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate">{e.label}</span>
          {e.step === 0 && shouldAutoStart(e.guide, seen) && <span aria-label="not seen yet" className="h-2 w-2 shrink-0 rounded-full bg-sky-500" />}
        </button>
      ))}
    </div>,
    document.body,
  );
}
